<template>
  <div>
    <div>
      <BaseLabel label="Request data" class="ml-4" />
      <BaseCollapse :items="requestItems" />
    </div>

    <div>
      <BaseLabel label="Response data" class="ml-4" />
      <BaseCollapse :items="responseItems" />
    </div>
  </div>
</template>

<script>

export default {
  name: 'ChatGPTActionDetailApi',

  props: {
    formData: {
      type: Object,
      required: true
    }
  },

  computed: {
    requestItems() {
      const requestItems = []
      if (this.showUnreleased) {
        requestItems.push({
          label: 'Options (UNRELEASED)',
          description: 'Configuration sent to the AI model.',
          value: this.formatValue(this.options)
        })
      }

      if (this.messageHistory.length) {
        requestItems.push({
          label: `Message history (${this.messageHistory.length})`,
          description: 'Messages sent to provide context to the model for the latest customer query.',
          value: this.formatValue(this.messageHistory)
        })
      }

      return requestItems
    },

    responseItems() {
      const responseItems = []
      if (this.contextBody) {
        responseItems.push({
          label: 'Response body',
          description: 'Aggregated response body from the API call.',
          value: this.formatValue(this.contextBody),
          isContext: true
        })
      }

      if (this.intents) {
        responseItems.push({
          label: 'Intent labels',
          description: 'The AI generated some guesses for the intent of the user\'s message.',
          value: this.formatValue(this.intents)
        })
      }

      if (this.citations) {
        responseItems.push({
          label: `Citations (${this.citations.length})`,
          description: `Citations of the top ${this.citations.length} documents that are most relevant to the user\'s message.`,
          value: this.formatValue(this.citations)
        })
      }

      if (this.proposedFilters) {
        responseItems.push({
          label: `Proposed filters (${this.proposedFilters.length})`,
          description: 'Filters the model proposed from the customer\'s message, before validation against the knowledge base.',
          value: this.formatValue(this.proposedFilters)
        })
      }

      if (this.appliedFilters) {
        responseItems.push({
          label: 'Applied filters',
          description: 'Validated filters actually applied to the vector search (per collection). Hallucinated fields/values are dropped.',
          value: this.formatValue(this.appliedFilters)
        })
      }

      return responseItems
    },
    
    messageHistory() {
      const messageHistory = this.formData?.data?.options?.messages
      if (!messageHistory) return []
      if (this.showUnreleased) return messageHistory

      return messageHistory.map(message => {
        if (
          message.content.startsWith('Use the JSON card format below ONLY when the customer is asking for product recommendations,') || // JSON_PROMPT
          message.content.startsWith('Your job is to answer customer emails with clear, accurate, and professional responses,') // EMAIL_PROMPT
        ) {
          return {
            ...message,
            content: '**AMIO PROMPT HIDDEN**'
          }
        }
        return message
      })
    },

    options() {
      return this.formData?.data?.options
    },

    contextBody() {
      return this.formData?.data?.context?.body
    },

    intents() {
      return this.formData?.data?.intent
    },

    citations() {
      if (!this.formData?.data?.citations) return null

      return this.formData.data.citations.map(citation => {
        return {
          ...citation,
          content: this.parseCitationContent(citation.content)
        }
      })
    },

    // RAG-search only: raw filters the model proposed (before validation)
    proposedFilters() {
      const proposed = this.formData?.data?.proposedFilters
      return proposed && proposed.length ? proposed : null
    },

    // RAG-search only: validated filters applied to the vector search, keyed by collection
    appliedFilters() {
      const filters = this.formData?.data?.filters
      return filters && Object.keys(filters).length ? filters : null
    }
  },
  methods: {
    // BaseTextPreview renders `value` as plain text (v-html sink); non-strings must be
    // stringified here or they render as "[object Object]".
    formatValue(value) {
      return typeof value === 'string' ? value : JSON.stringify(value, null, 2)
    },

    parseCitationContent(content) {
      if (!content || typeof content !== 'string') return content

      try {
        // First try to parse as JSON
        return JSON.parse(content)
      } catch {
        // If not JSON, try to parse as comma-separated key-value pairs (flat product payloads).
        // PRO-363: Qdrant citations are usually plain document prose, which this fallback used to
        // shred into an empty object (no segment has a colon) or a bogus one-key object (prose with
        // an incidental colon), losing the text. So only believe the key-value reading when EVERY
        // segment parses as a pair AND there is more than one of them - a single "key: value" is
        // indistinguishable from a sentence with a colon, and prose must always survive verbatim.
        const obj = {}
        const pairs = content.split(',').map(pair => pair.trim()).filter(pair => pair)

        for (const pair of pairs) {
          const colonIndex = pair.indexOf(':')
          if (colonIndex <= 0) return content

          const key = pair.substring(0, colonIndex).trim()
          const value = pair.substring(colonIndex + 1).trim()

          // Try to parse numeric values
          obj[key] = !isNaN(value) && value !== '' ? Number(value) : value
        }

        return pairs.length > 1 ? obj : content
      }
    }
  }
}
</script>
